/**
 * Ships non-error console output and failed fetches to the parent (builder)
 * frame as `VIBE_RUNTIME_LOG` postMessages. The builder batches these and
 * flushes them to the runtime-logs ingest endpoint, where the Vibe agent can
 * read them via its read-only `/logs` mount.
 *
 * console.error and thrown errors are NOT handled here — they go through the
 * richer error-reporting pipeline (sourcemap translation, React-warning
 * filtering) in `index.ts`, and the parent merges both streams.
 *
 * Fire-and-forget by design: when the runtime-logs feature is disabled the
 * parent simply ignores these messages.
 */

const MAX_MSG_CHARS = 2000;

// Rate limit on the number of individual log lines we ship (one console call
// or one failed fetch = one line). A short burst is fine; sustained spam from
// a render loop is dropped so it can't flood the parent or the agent.
const MAX_BURST_LINES = 300; // lines allowed in a burst
const SUSTAINED_LINES_PER_SECOND = 5; // ~300 lines/min once the burst is spent
let availableLines = MAX_BURST_LINES;
let lastRefillAt = Date.now();
let droppedLines = 0;

type LogLevel = 'log' | 'info' | 'warn' | 'debug' | 'error' | 'meta';

/**
 * Builder origin, derived from the embedding page (same pattern as
 * router-sync.js). Resolves to `null` when the referrer is unavailable
 * (privacy settings, same-origin navigations); in that case the shipper goes
 * silent rather than broadcasting console output to an arbitrary embedder
 * with a `'*'` target.
 */
const parentOrigin = getParentOrigin();

function getParentOrigin(): string | null {
  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}

let initialized = false;

export function initLogShipper(): void {
  if (initialized) return;
  initialized = true;

  interceptConsole();
  interceptFetchFailures();
}

function interceptConsole(): void {
  for (const level of ['log', 'info', 'warn', 'debug'] as const) {
    const original = console[level];
    console[level] = (...args: unknown[]) => {
      original.apply(console, args);
      shipSafely(level, () => args.map(stringifyArg).join(' '));
    };
  }
}

function interceptFetchFailures(): void {
  const originalFetch = window.fetch;
  if (typeof originalFetch !== 'function') return;

  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const target = describeFetchTarget(args[0]);
    // Fire-and-forget analytics/telemetry beacons (e.g. BigBrain) are commonly blocked by
    // ad-blockers or drop on flaky networks; their failures are not app errors, so never ship them.
    const isAnalyticsBeacon = /bigbrain/i.test(target);
    try {
      const response = await originalFetch.apply(window, args);
      if (!response.ok && !isAnalyticsBeacon) {
        shipSafely('error', () => `fetch failed: ${target} -> HTTP ${response.status} ${response.statusText || ''}`.trim());
      }
      return response;
    } catch (error: any) {
      if (!isAnalyticsBeacon) {
        shipSafely('error', () => `fetch network error: ${target} -> ${error?.message || String(error)}`);
      }
      throw error;
    }
  };
}

/** Shipping must never break the app or interfere with a response. */
function shipSafely(level: LogLevel, buildMessage: () => string): void {
  try {
    ship(level, buildMessage());
  } catch {
    // Silent by design.
  }
}

function describeFetchTarget(input: RequestInfo | URL): string {
  try {
    if (typeof input === 'string') return input;
    if (input instanceof Request) return `${input.method} ${input.url}`;
    if (input instanceof URL) return input.href;
    return String(input);
  } catch {
    return 'unknown';
  }
}

function stringifyArg(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

// Returns true if there's budget to ship one more line, refilling the
// allowance based on how much time has passed since the last call.
function allowLine(): boolean {
  const now = Date.now();
  availableLines = Math.min(MAX_BURST_LINES, availableLines + ((now - lastRefillAt) / 1000) * SUSTAINED_LINES_PER_SECOND);
  lastRefillAt = now;
  if (availableLines < 1) {
    droppedLines += 1;
    return false;
  }
  availableLines -= 1;
  return true;
}

function ship(level: LogLevel, message: string): void {
  if (!window.top || window.top === window) return;
  // No verifiable builder origin -> never broadcast logs to an unknown target.
  if (!parentOrigin) return;
  if (!allowLine()) return;

  if (droppedLines > 0) {
    postLine('meta', `log shipper dropped ${droppedLines} log line(s) (rate cap)`);
    droppedLines = 0;
  }
  postLine(level, message);
}

function postLine(level: LogLevel, message: string): void {
  if (!window.top || !parentOrigin) return;
  window.top.postMessage(
    {
      type: 'VIBE_RUNTIME_LOG',
      level,
      message: String(message).slice(0, MAX_MSG_CHARS),
      ts: Date.now(),
    },
    parentOrigin,
  );
}
